import type { Icon } from '../../design-system/icons';
import { Skeleton } from '../ui/skeleton';
import { cn } from '../../lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

export interface KpiCardProps {
  /** Duotone Phosphor glyph shown in the stone-100 round box. */
  icon: Icon;
  label: string;
  value: string | number;
  valueTooltip?: string;
  labelAddon?: React.ReactNode;
  description?: string;
  loading?: boolean;
  compact?: boolean;
}

/**
 * Shared campaign KPI stat card (Figma 1672:33334): a stone-100 round icon box
 * on the left, label + big value on the right. No delta, no progress bar.
 *
 * Single source of truth for the reporting "General Insights" strip and the
 * Creators / Finalized roster KPI strips (via RosterKpiCard).
 */
export function KpiCard({
  icon: IconCmp,
  label,
  value,
  valueTooltip,
  labelAddon,
  description,
  loading = false,
  compact = false,
}: KpiCardProps) {
  return (
    <div
      // Stable hook for tests that assert a surface has NO KPI strip — notably
      // the public (no-login) share shell, which must never show campaign-wide
      // aggregates the link didn't grant.
      data-testid="kpi-card"
      className={cn(
        'border-border bg-card flex items-center gap-3 rounded-xl border px-4 py-[18px]',
        compact && 'gap-2 px-3',
      )}
    >
      <div
        className={cn(
          'flex size-12 shrink-0 items-center justify-center rounded-full bg-stone-100',
          compact && 'size-10',
        )}
      >
        <IconCmp size={compact ? 24 : 30} weight="duotone" className="text-foreground" />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex min-w-0 items-center gap-1">
          <p className="text-body-sm-regular text-foreground truncate" title={label}>
            {label}
          </p>
          {labelAddon}
        </div>
        {loading ? (
          <Skeleton className="h-7 w-20" />
        ) : valueTooltip ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <p
                tabIndex={0}
                className={cn(
                  'text-heading-h4/9 text-foreground focus-visible:ring-ring truncate rounded-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-inset',
                  compact && 'text-heading-h5/8',
                )}
              >
                {value}
              </p>
            </TooltipTrigger>
            <TooltipContent data-clarity-mask="True">{valueTooltip}</TooltipContent>
          </Tooltip>
        ) : (
          <p
            className={cn(
              'text-heading-h4/9 text-foreground truncate font-medium',
              compact && 'text-heading-h5/8',
            )}
          >
            {value}
          </p>
        )}
        {description ? (
          <p className="text-caption-regular text-muted-foreground">{description}</p>
        ) : null}
      </div>
    </div>
  );
}
